import { useSyncExternalStore } from 'react';
import { DEFAULT_FILTER_MODEL } from 'src/constants';

const STORAGE_KEY = 'careit-page-filters';

const listeners = new Set();

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function cloneFilterModel(filterModel = DEFAULT_FILTER_MODEL) {
  return {
    ...filterModel,
    items: Array.isArray(filterModel.items)
      ? filterModel.items.map((item) => ({ ...item }))
      : [],
  };
}

function createDefaultState() {
  return {
    default: cloneFilterModel(DEFAULT_FILTER_MODEL),
  };
}

function normalizeState(rawState) {
  const nextState = createDefaultState();

  if (!isPlainObject(rawState)) {
    return nextState;
  }

  for (const [page, filterModel] of Object.entries(rawState)) {
    if (isPlainObject(filterModel) && Array.isArray(filterModel.items)) {
      nextState[page] = cloneFilterModel(filterModel);
    }
  }

  if (!nextState.default) {
    nextState.default = cloneFilterModel(DEFAULT_FILTER_MODEL);
  }

  return nextState;
}

function readStorage() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return createDefaultState();
  }

  try {
    const rawState = window.localStorage.getItem(STORAGE_KEY);
    if (!rawState) {
      return createDefaultState();
    }

    return normalizeState(JSON.parse(rawState));
  } catch {
    return createDefaultState();
  }
}

let state = readStorage();

function persistState() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage quota / availability errors.
  }
}

function notify() {
  listeners.forEach((listener) => listener());
}

function updateState(nextState) {
  state = normalizeState(nextState);
  persistState();
  notify();
}

function handleStorageEvent(event) {
  if (event.key !== STORAGE_KEY) {
    return;
  }

  state = readStorage();
  notify();
}

if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', handleStorageEvent);
}

function getPageFiltersState() {
  return state;
}

function subscribePageFilters(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setPageFilter(page, data) {
  updateState({
    ...state,
    [page]: cloneFilterModel(data),
  });
}

function resetPageFilters() {
  updateState(createDefaultState());
}

function usePageFiltersStore() {
  return useSyncExternalStore(
    subscribePageFilters,
    getPageFiltersState,
    getPageFiltersState
  );
}

export {
  STORAGE_KEY,
  cloneFilterModel,
  getPageFiltersState,
  resetPageFilters,
  setPageFilter,
  subscribePageFilters,
  usePageFiltersStore,
};
